import { type MouseEvent, type PropsWithChildren, useEffect, useRef } from "react";
import { css, keyframes, styled } from "styled-components";

import { UIButton } from "common/components";
import { breakpoints } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getModalParams } from "domains/modal/selectors";
import type { ModalContentType, ModalParams } from "domains/modal/types";
import { lightTheme } from "domains/theme/constants/theme";

const StyledContainer = styled.div<{ isAlwaysWhite: boolean }>`
  background-color: ${({ theme, isAlwaysWhite }) =>
    isAlwaysWhite ? theme.colors.white : theme.colors.modalBackground};
  width: 100%;
  box-sizing: border-box;
  padding: 0 1rem;
  min-height: 100%;

  ${({ isAlwaysWhite }) =>
    isAlwaysWhite &&
    css`
      input {
        color: black;
      }
    `}

  @media (min-width: 480px) {
    padding: 0 2rem;
  }

  @media ${breakpoints.large} {
    padding: 0 3rem;
  }
`;

const cssAnimation = keyframes`
  0% {
    opacity: 0;
  }
  90% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const StyledInnerContainer = styled.div<{ hasDelay: boolean }>`
  padding-bottom: 2rem;

  ${({ hasDelay }) =>
    hasDelay &&
    css`
      animation: ${cssAnimation} 400ms;
    `}

  @media ${breakpoints.large} {
    padding-bottom: 2.5rem;
  }
`;

const StyledButton = styled(UIButton)<{ isAlwaysWhite: boolean }>`
  ${({ isAlwaysWhite }) =>
    isAlwaysWhite &&
    css`
      border-color: ${lightTheme.colors.buttonOutlinedText};
      color: ${lightTheme.colors.buttonOutlinedText};

      &:focus-visible,
      &:hover {
        background-color: ${lightTheme.colors.buttonActiveBorder};
        border-color: ${lightTheme.colors.buttonActiveBorder};
        color: ${lightTheme.colors.buttonOutlinedActiveText};
      }
    `}
`;

type ModalProps = PropsWithChildren & { dismiss: () => void; title?: string };

export const Modal = ({ children, dismiss }: ModalProps) => {
  const params = useSelector(getModalParams) as ModalParams<ModalContentType>;
  const translate = useTranslate();

  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const handleDeactivateClick = (event: MouseEvent) => {
    event.preventDefault();
    dismiss();
  };

  useEffect(() => {
    previousActiveElement.current = document.activeElement as HTMLElement;
    containerRef.current?.focus();

    const closeModalOnEscapeKeyPress = (e: KeyboardEvent) => {
      if (e.code === "Escape") {
        dismiss();
      }
    };
    window.addEventListener("keydown", closeModalOnEscapeKeyPress);

    return () => {
      window.removeEventListener("keydown", closeModalOnEscapeKeyPress);
      previousActiveElement.current?.focus();
    };
  }, [dismiss]);

  return (
    <StyledContainer ref={containerRef} role="dialog" tabIndex={0} isAlwaysWhite={params.isAlwaysWhite ?? false}>
      {children}
      <StyledInnerContainer hasDelay={params.hasDelay ?? false}>
        <StyledButton
          variant="outlined"
          fullwidth
          style={{ marginTop: "1em" }}
          onClick={handleDeactivateClick}
          isAlwaysWhite={params.isAlwaysWhite ?? false}
        >
          {translate("app.faq_teaser.dismiss")}
        </StyledButton>
      </StyledInnerContainer>
    </StyledContainer>
  );
};
